/**
 * TaskColumnsEditor.jsx
 *
 * Renders a draggable and reorderable list of task columns for the task editor view.
 * Includes a fixed, disabled special column at the top.
 * Uses react-dnd for drag-and-drop and Material UI for styling.
 *
 * Props:
 * - columns: An array of column objects to display in the list.
 * - onChange: Callback triggered when a column's visibility is toggled.
 * - onMove: Callback triggered when columns are reordered through drag-and-drop.
 */

import PropTypes from "prop-types";
import { memo, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import List from "@mui/material/List";
import { StandardColumnIds } from "../../helpers/columnUtils";
import { ProjectViewType } from "../../ProjectViews/projectViewUtils";
import CustomDragLayer from './CustomDragLayer';
import TaskColumnsEditorItem from "./TaskColumnsEditorItem";

/**
 * Special fixed column item.
 *
 * This column is always visible and represents a fixed column in other task views or interfaces.
 * It is read-only and cannot be moved or toggled.
 *
 * The `fieldId` is a unique identifier used internally for drag-and-drop operations and state management.
 * For this special column, we use the TASK_NAME standard column id that should not conflict with
 * any other column's `fieldId` in the list.
 *
 * Using a reserved or clearly distinguishable `fieldId` helps avoid accidental
 * reordering or toggling actions on this fixed item.
 *
 * The index is set to -1 to avoid conflicts with indices of regular columns.
 * This value is used internally to distinguish this special item from others.
 */
const TaskColumnItemComponent = () => (
  <TaskColumnsEditorItem
    column={{
      checked: true,
      fieldId: StandardColumnIds.TASK_NAME,
      name: <FormattedMessage id="project.standardColumn.task" />,
      type: ProjectViewType.Tasks,
    }}
    disabled
    index={-1}
  />
);

const TaskColumnItem = memo(TaskColumnItemComponent);

/**
 * @typedef {Object} TaskColumn
 * Represents a single column item in the task view editor.
 *
 * @property {boolean} checked - Whether the column is visible (selected).
 * @property {string|number} fieldId - Unique identifier for the column.
 * @property {React.ReactNode} name - Display name or label of the column.
 * @property {number} [type] - Numeric column type (used for icon rendering).
 *                             Optional only for special, non-interactive columns.
 */

/**
 * TaskColumnsEditor
 *
 * Renders a list of task view columns with drag-and-drop reordering.
 * Includes a fixed special column at the top which is disabled and cannot be moved.
 * Wraps the list in a react-dnd DndProvider with the HTML5 backend.
 *
 * @param {Object} props - Component props.
 * @param {TaskColumn[]} props.columns - List of columns to render.
 * @param {(fieldId: string|number, checked: boolean) => void} props.onChange - Called when a column's visibility is toggled.
 * @param {(fromIndex: number, toIndex: number) => void} props.onMove - Called when a column is reordered via drag-and-drop.
 * @returns {JSX.Element}
 */
const TaskColumnsEditor = ({
  columns,
  onChange,
  onMove,
}) => {
  const listRef = useRef();

  /**
   * Only columns with a numeric fieldId are treated as orderable.
   * This is due to a limitation of TableView not allowing reordering of non dynamic columns.
   * 
   * TODO: Fix TableView column reordering.
   */
  const checkIfDisabledDnd = (fieldId) => !Number.isFinite(fieldId);

  return (
    <DndProvider backend={HTML5Backend}>
      <List ref={listRef}>
        <TaskColumnItem key="fixed-task-column" />
        {columns.filter(n => n.fieldId !== StandardColumnIds.TASK_NAME).map((field, index) => (
          <TaskColumnsEditorItem
            column={field}
            disableDnd={checkIfDisabledDnd(field.fieldId)}
            index={index}
            key={field.fieldId}
            onChange={onChange}
            onMove={onMove}
          />
        ))}
      </List>
      <CustomDragLayer
        columns={columns}
        containerRef={listRef}
        onChange={onChange}
        onMove={onMove}
      />
    </DndProvider>
  );
};

TaskColumnsEditor.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      checked: PropTypes.bool.isRequired,
      fieldId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.node.isRequired,
      type: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired, // (fieldId: string|number, checked: boolean) => void
  onMove: PropTypes.func.isRequired,   // (fromIndex: number, toIndex: number) => void
};

export default memo(TaskColumnsEditor);
